/**
 * Quiz Context
 * Global state management for quiz flow
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import type {
  QuizType,
  Question,
  QuizAnswer,
  QuizResult
} from '../types';
import { quizAPI } from '../api/quiz-api';

interface QuizContextType {
  // State
  quizTypes: QuizType[];
  currentQuizType: QuizType | null;
  questions: Question[];
  answers: QuizAnswer[];
  currentQuestionIndex: number;
  results: QuizResult | null;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;

  // Actions
  loadQuizTypes: () => Promise<void>;
  startQuiz: (quizTypeId: number) => Promise<void>;
  answerQuestion: (questionId: number, value: any) => void;
  goToNextQuestion: () => void;
  goToPreviousQuestion: () => void;
  submitQuiz: (userId?: number) => Promise<void>;
  loadResults: (sessionId: string) => Promise<void>;
  resetQuiz: () => void;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

export const QuizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [quizTypes, setQuizTypes] = useState<QuizType[]>([]);
  const [currentQuizType, setCurrentQuizType] = useState<QuizType | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [results, setResults] = useState<QuizResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const loadQuizTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const types = await quizAPI.getTypes();
      setQuizTypes(types);
    } catch (err: any) {
      setError(err.message || 'Failed to load quiz types');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startQuiz = useCallback(async (quizTypeId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await quizAPI.getQuestions(quizTypeId);
      setCurrentQuizType(data.quizType);
      setQuestions(data.questions);
      setAnswers([]);
      setCurrentQuestionIndex(0);
      setResults(null);

      // Generate unique session ID
      const newSessionId = `quiz-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
    } catch (err: any) {
      setError(err.message || 'Failed to load quiz questions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const answerQuestion = useCallback((questionId: number, value: any) => {
    setAnswers(prev => {
      const existing = prev.findIndex(a => a.questionId === questionId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { questionId, value };
        return updated;
      }
      return [...prev, { questionId, value }];
    });
  }, []);

  const goToNextQuestion = useCallback(() => {
    setCurrentQuestionIndex(prev => Math.min(prev + 1, questions.length - 1));
  }, [questions.length]);

  const goToPreviousQuestion = useCallback(() => {
    setCurrentQuestionIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const submitQuiz = useCallback(async (userId?: number) => {
    if (!sessionId || !currentQuizType) {
      setError('Quiz not started');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await quizAPI.submitQuiz({
        sessionId,
        quizTypeId: currentQuizType.id,
        answers,
        userId,
      });

      setResults(result);
    } catch (err: any) {
      setError(err.message || 'Failed to submit quiz');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, currentQuizType, answers]);

  const loadResults = useCallback(async (sessionId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await quizAPI.getResults(sessionId);
      if (result) {
        setResults(result);
        setSessionId(sessionId);
      } else {
        setError('Results not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load results');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetQuiz = useCallback(() => {
    setCurrentQuizType(null);
    setQuestions([]);
    setAnswers([]);
    setCurrentQuestionIndex(0);
    setResults(null);
    setSessionId(null);
    setError(null);
  }, []);

  const value: QuizContextType = {
    quizTypes,
    currentQuizType,
    questions,
    answers,
    currentQuestionIndex,
    results,
    isLoading,
    error,
    sessionId,
    loadQuizTypes,
    startQuiz,
    answerQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    submitQuiz,
    loadResults,
    resetQuiz,
  };

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
};

export const useQuiz = (): QuizContextType => {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error('useQuiz must be used within QuizProvider');
  }
  return context;
};
